/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { InscricaoService } from 'src/app/services/inscricao.service'
import { UnespCoreAuthService, UnespCoreMessageService, UnespCoreUserService } from 'src/libs/unesp-core'
import { ActivatedRoute, Router } from '@angular/router'
import { Sexo, ORDERED_SEXO_OPTIONS } from 'src/app/enums/sexo'
import { EstadoCivil } from 'src/app/enums/estado-civil'
import { Estado } from 'src/app/enums/estado'
import { OrgaoExpedicaoRg } from 'src/app/enums/orgao-expedicao-rg'
import { InscricaoFicha } from 'src/app/models/inscricao-ficha'
import { Estrangeiro } from 'src/app/enums/estrangeiro'
import { Deficiencia } from 'src/app/enums/deficiencia'
import { GrauEscolaridade } from 'src/app/enums/grau-escolaridade'
import { InscricaoComplemento } from 'src/app/models/inscricao-complemento'
import {
  ArquivoSelecionado,
  ArquivoStatus,
  arquivoUploadValidator,
} from '../../arquivo-upload/arquivo-upload.component'
import { Anexo } from 'src/app/models/anexo'
import { DatePipe } from '@angular/common'
import { InscricaoDocumento } from 'src/app/models/inscricao-documento'
import { ValidacaoService } from 'src/app/services/validacao.service'
import { Autodeclaracao } from 'src/app/enums/autodeclaracao'
import { TipoConcurso } from 'src/app/enums/tipo-concurso'
import { MatTabGroup } from '@angular/material/tabs'
import { maskDocumentoMigratorio } from 'src/app/utils/rne-rnm-validator'

@Component({
  selector: 'app-inscricao-ficha',
  templateUrl: './inscricao-ficha.component.html',
  styleUrls: ['./inscricao-ficha.component.scss'],
})
export class InscricaoFichaComponent implements OnInit {
  @ViewChild('tabGroup') tabGroup!: MatTabGroup
  @ViewChild('estabilidadeSection') estabilidadeSection?: ElementRef
  parametroPagina = this.route.snapshot.queryParamMap.get('pagina')
  parametroTamanho = this.route.snapshot.queryParamMap.get('tamanho')
  parametroTab = this.route.snapshot.queryParamMap.get('tab')
  inscricaoFicha?: InscricaoFicha
  tipoConcurso?: TipoConcurso
  datepipe: DatePipe = new DatePipe('pt-BR')
  laudoMedicoPcdFc: FormControl<ArquivoSelecionado>
  laudoMedicoAtendimentoEspecialFc: FormControl<ArquivoSelecionado>
  documentoIdentificacaoFc: FormControl<ArquivoSelecionado>
  requerimentoNomeSocialFc: FormControl<ArquivoSelecionado>

  isPeriodoDeInscricao: boolean = false
  isPeriodoSegundaFase: boolean = false
  inscricaoEditavel: boolean = false
  shouldHideDownloadPrintIcons: boolean = false
  edicaoFicha: boolean = false
  edicaoComplementos: boolean = false
  sexo = Sexo
  identidadeGenero: string = ''
  estadoCivil = EstadoCivil
  estado = Estado
  orgaoExpedicaoRg = OrgaoExpedicaoRg
  estrangeiro = Estrangeiro
  grauEscolaridade = GrauEscolaridade
  deficiencia = Deficiencia
  autodeclaraRaca: boolean | string = false
  pontuacaoDiferenciada: boolean | string = false
  autodeclaracao = Autodeclaracao
  declaracaoVeracidade: boolean = false
  temas: string[] = []
  complementos: InscricaoComplemento[] = []
  mapGruposComplementos: Map<string, InscricaoComplemento[]> = new Map()
  complementosInscricaoStatus: 'Completo' | 'Falta' | 'Faltou' = 'Faltou'
  complementosInscricaoStatusMsg: string = ''
  temSegundaFase: boolean = false
  complementosSegundaFaseStatus: 'Completo' | 'Falta' | 'Faltou' = 'Faltou'
  complementosSegundaFaseStatusMsg: string = ''
  anexos: Anexo[] = []
  documentos: InscricaoDocumento[] = []
  id?: number
  concursoId?: number
  isDepartamento: boolean = false
  formComplementar = new FormGroup({
    inscricao: new FormGroup({}),
    segundaFase: new FormGroup({}),
  })

  idConcurso: number = 0

  // Validação de datas
  readonly today = new Date()

  // Histórico de alterações
  historicoLogs: any[] = []
  historicoPage = 0
  historicoTotalElements = 0
  historicoPageSize = 25
  historicoLoading = false
  historicoInfoExpanded = false
  estabilidadeLogs: any[] = []
  estabilidadeLoading = false
  estabilidadeLogRef: any = null
  estabilidadePage = 0
  estabilidadeTotalElements = 0

  private acaoTraducao: Record<string, string> = {
    'INSERT_INSCRICAO': 'Criação da inscrição',
    'UPDATE_INSCRICAO': 'Alteração da inscrição',
    'DELETE_INSCRICAO': 'Exclusão da inscrição',
    'INSERT_INSCRICAO_COMPLEMENTO': 'Tentativa de upload de complemento',
    'UPDATE_INSCRICAO_COMPLEMENTO': 'Complemento registrado',
    'DELETE_INSCRICAO_COMPLEMENTO': 'Exclusão de complemento',
    'INSERT_INSCRICAO_ARQUIVO': 'Upload de arquivo',
    'UPDATE_INSCRICAO_ARQUIVO': 'Alteração de arquivo',
    'DELETE_INSCRICAO_ARQUIVO': 'Exclusão de arquivo',
    'INSERT_INSCRICAO_DOCUMENTO': 'Upload de documento',
    'UPDATE_INSCRICAO_DOCUMENTO': 'Alteração de documento',
    'DELETE_INSCRICAO_DOCUMENTO': 'Exclusão de documento',
    'LOGIN': 'Login',
    'ACEITE_LGPD_CONSENTIMENTO_LGPD': 'Aceite LGPD',
    'INSERT_COMISSAO_RESULTADO_INSCRICAO': 'Registro de classificação',
    'UPDATE_COMISSAO_RESULTADO_INSCRICAO': 'Alteração de classificação',
    'DELETE_COMISSAO_RESULTADO_INSCRICAO': 'Exclusão de classificação',
    'INSERT_COMISSAO_MALOTE_RESULTADO_PROVA': 'Registro de resultado de prova',
    'UPDATE_COMISSAO_MALOTE_RESULTADO_PROVA': 'Alteração de resultado de prova',
    'DELETE_COMISSAO_MALOTE_RESULTADO_PROVA': 'Exclusão de resultado de prova',
    'INSERT_COMISSAO_NOTA_PROVA_INSCRICAO': 'Registro de nota de prova',
    'UPDATE_COMISSAO_NOTA_PROVA_INSCRICAO': 'Alteração de nota de prova',
    'DELETE_COMISSAO_NOTA_PROVA_INSCRICAO': 'Exclusão de nota de prova',
    'INSERT_INSCRICAO_NOTA_PROVA': 'Registro de nota de prova',
    'UPDATE_INSCRICAO_NOTA_PROVA': 'Alteração de nota de prova',
    'DELETE_INSCRICAO_NOTA_PROVA': 'Exclusão de nota de prova',
    'INSERT_RECURSO': 'Interposição de recurso',
    'INSERT_INSCRICAO_UPLOAD': 'Upload de documento',
    'UPDATE_INSCRICAO_UPLOAD': 'Upload de documento',
    'DELETE_INSCRICAO_UPLOAD': 'Exclusão de documento',
    'UPDATE_RECURSO': 'Alteração de recurso',
    'DELETE_RECURSO': 'Exclusão de recurso',
  }


  form = new FormGroup({
    idConcurso: new FormControl('', Validators.required),
    tituloConcurso: new FormControl('', Validators.required),
    campus: new FormControl('', Validators.required),
    pago: new FormControl('', Validators.required),
    data: new FormControl('', Validators.required),
    protocolo: new FormControl('', Validators.required),
    cpf: new FormControl('', Validators.required),
    nome: new FormControl('', Validators.required),
    dataNascimento: new FormControl(new Date(), Validators.required),
    nomeSocial: new FormControl(''),
    nomeMae: new FormControl(''),
    nomePai: new FormControl(''),
    profissao: new FormControl('', Validators.required),
    escolaridade: new FormControl('', Validators.required),
    sexo: new FormControl('', Validators.required),
    identidadeGenero: new FormControl(''),
    estadoCivil: new FormControl('', Validators.required),
    estrangeiro: new FormControl('', Validators.required),
    passaporte: new FormControl(''),
    rne: new FormControl(''),
    rg: new FormControl({ value: '', disabled: true }),
    uf: new FormControl({ value: '', disabled: true }),
    dataExpedicao: new FormControl(new Date()),
    orgaoExpedicao: new FormControl({ value: '', disabled: true }),
    pais: new FormControl('', Validators.required),
    estado: new FormControl('', Validators.required),
    cidade: new FormControl('', Validators.required),
    celular: new FormControl('', Validators.required),
    telefone: new FormControl(''),
    email: new FormControl('', Validators.required),
    enderecoPais: new FormControl('', Validators.required),
    enderecoCep: new FormControl('', Validators.required),
    enderecoLogradouro: new FormControl('', Validators.required),
    enderecoNumero: new FormControl('', Validators.required),
    enderecoBairro: new FormControl('', Validators.required),
    enderecoCidade: new FormControl('', Validators.required),
    enderecoEstado: new FormControl('', Validators.required),
    enderecoComplemento: new FormControl(''),
    canhoto: new FormControl('', Validators.required),
    lactante: new FormControl('', Validators.required),
    concorrerVagaDeficiente: new FormControl('', Validators.required),
    autodeclaraRaca: new FormControl(''),
    pontuacaoDiferenciada: new FormControl(''),
    autodeclaracao: new FormControl(''),
    declaracaoVeracidade: new FormControl(''),
    possuiDeficiencia: new FormControl(''),
    deficiencia: new FormControl(),
    deficienciaNecessidadesProva: new FormControl(),
    temas: new FormControl(),
    possuiOutraNecessidade: new FormControl(),
    atendimentoEspecialNecessidadesProva: new FormControl(),

    provaIngles: new FormControl('', Validators.required),
  })

  constructor(
    private inscricaoService: InscricaoService,
    private unespCoreMessageService: UnespCoreMessageService,
    private unespCoreUserService: UnespCoreUserService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private unespCoreAuthService: UnespCoreAuthService,
    private router: Router,
    private validacaoService: ValidacaoService
  ) {
    this.laudoMedicoPcdFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
      updateOn: 'submit',
    }) as FormControl<ArquivoSelecionado>
    this.laudoMedicoPcdFc.disable()
    this.documentoIdentificacaoFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
      updateOn: 'submit',
    }) as FormControl<ArquivoSelecionado>
    this.documentoIdentificacaoFc.disable()
    this.requerimentoNomeSocialFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
      updateOn: 'submit',
    }) as FormControl<ArquivoSelecionado>
    this.requerimentoNomeSocialFc.disable()
    this.laudoMedicoAtendimentoEspecialFc = new FormControl(new ArquivoSelecionado(), {
      validators: [arquivoUploadValidator(false)],
      updateOn: 'submit',
    }) as FormControl<ArquivoSelecionado>
    this.laudoMedicoAtendimentoEspecialFc.disable()

  }

  ngOnInit(): void {
    this.isDepartamento = this.unespCoreAuthService.hasPermissionBasedRoles(['DEPARTAMENTO'])
    this.route.params.subscribe(params => {
      this.id = params['id']
      this.concursoId = params['concursoId']
      this.form.reset()
      this.cd.detectChanges()

      if (this.id) {
        this.inscricaoService.get(this.id).subscribe(obj => {
          this.inscricaoFicha = obj
          this.tipoConcurso = obj.tipoConcurso
          this.documentos = obj.inscricaoDocumento
          this.inscricaoService.temas(Number(obj.id)).subscribe(temas => {
            this.temas = temas
          })
          this.form.get('idConcurso')?.setValue(obj.idConcurso.toString())
          this.idConcurso = Number(obj.idConcurso!)
          if (obj.periodoInscricao as boolean) {
            this.isPeriodoDeInscricao = true
            this.inscricaoEditavel = true
          }
          if (obj.periodoSegundaFase as boolean) {
            this.isPeriodoSegundaFase = true
          }

          if (
            this.unespCoreUserService.getUser()?.roles.includes('GERENTE') ||
            this.unespCoreUserService.getUser()?.roles.includes('RH')
          )
            this.inscricaoEditavel = true

          // Ocultar ícones de download/impressão para todos exceto SUPERADMINS
          const isSuperAdmin = this.unespCoreAuthService.hasPermissionBasedRoles(['SUPERADMIN'])
          this.shouldHideDownloadPrintIcons = !isSuperAdmin

          this.form.get('tituloConcurso')?.setValue(obj.tituloConcurso)
          this.form.get('campus')?.setValue(obj.campus)
          this.form.get('pago')?.setValue(obj.pago ? 'Confirmado' : 'Pendente')
          this.form.get('data')?.setValue(obj.data)
          this.form.get('protocolo')?.setValue(obj.protocolo)
          this.form.get('cpf')?.setValue(this.formatCPF(obj.cpf))
          this.form.get('nome')?.setValue(obj.nome)
          this.form.get('dataNascimento')?.setValue(obj.dataNascimento as any)
          this.form.get('nomeSocial')?.setValue(obj.nomeSocial)
          this.form.get('nomeMae')?.setValue(obj.nomeMae)
          this.form.get('nomePai')?.setValue(obj.nomePai)
          this.form.get('profissao')?.setValue(obj.profissao)
          this.form.get('escolaridade')?.setValue(obj.escolaridade)
          this.form.get('sexo')?.setValue(obj.sexo)
          this.form.get('identidadeGenero')?.setValue(obj.identidadeGenero)
          this.form.get('estadoCivil')?.setValue(obj.estadoCivil)
          this.form.get('estrangeiro')?.setValue(obj.estrangeiro)
          this.form.get('passaporte')?.setValue(obj.passaporte)
          this.form.get('rne')?.setValue(obj.rne)
          this.form.get('rg')?.setValue(obj.rg)
          this.form.get('uf')?.setValue(obj.uf)
          this.form
            .get('dataExpedicao')
            ?.setValue(
              obj.dataExpedicao !== null && obj.dataExpedicao !== ''
                ? obj.dataExpedicao as any
                : null
            )
          this.form.get('orgaoExpedicao')?.setValue(obj.orgaoExpedicao)
          this.form.get('pais')?.setValue(obj.pais)
          this.form.get('estado')?.setValue(obj.estado)
          this.form.get('cidade')?.setValue(obj.cidade)
          this.form.get('celular')?.setValue(obj.celular)
          this.form.get('telefone')?.setValue(obj.telefone)
          this.form.get('email')?.setValue(obj.email)
          this.form.get('enderecoPais')?.setValue(obj.enderecoPais)
          this.form.get('enderecoCep')?.setValue(obj.enderecoCep)
          this.form.get('enderecoLogradouro')?.setValue(obj.enderecoLogradouro)
          this.form.get('enderecoNumero')?.setValue(obj.enderecoNumero)
          this.form.get('enderecoBairro')?.setValue(obj.enderecoBairro)
          this.form.get('enderecoCidade')?.setValue(obj.enderecoCidade)
          this.form.get('enderecoEstado')?.setValue(obj.enderecoEstado)
          this.form.get('enderecoComplemento')?.setValue(obj.enderecoComplemento)

          this.form.get('canhoto')?.setValue(obj.canhoto ? 'true' : 'false')
          this.form.get('lactante')?.setValue(obj.lactante ? 'true' : 'false')
          this.form.get('possuiOutraNecessidade')?.setValue(obj.possuiOutraNecessidade ? 'true' : 'false')
          this.form.get('atendimentoEspecialNecessidadesProva')?.setValue(obj.atendimentoEspecialNecessidadesProva)
          this.laudoMedicoAtendimentoEspecialFc.value.setNameAndSize(
            obj.atendimentoEspecialLaudoMedico,
            obj.atendimentoEspecialLaudoMedicoTamanho
          )
          this.laudoMedicoAtendimentoEspecialFc.setValue(this.laudoMedicoAtendimentoEspecialFc.value)

          this.form.get('possuiDeficiencia')?.setValue(obj.possuiDeficiencia ? 'true' : 'false')
          this.form.get('deficiencia')?.setValue(obj.deficiencia)
          this.form.get('deficienciaNecessidadesProva')?.setValue(obj.deficienciaNecessidadesProva)
          this.form.get('concorrerVagaDeficiente')?.setValue(obj.concorrerVagaDeficiente ? 'true' : 'false')
          this.laudoMedicoPcdFc.value.setNameAndSize(obj.deficienciaLaudoMedico, obj.deficienciaLaudoMedicoTamanho)
          this.laudoMedicoPcdFc.setValue(this.laudoMedicoPcdFc.value)


          this.form.get('provaIngles')?.setValue(obj.provaIngles ? 'true' : 'false')
          if (this.tipoConcurso.match('TECADM')) {
            this.form.get('autodeclaraRaca')?.setValue(obj.autodeclaraRaca ? 'true' : 'false')
            this.form.get('pontuacaoDiferenciada')?.setValue(obj.pontuacaoDiferenciada ? 'true' : 'false')
            this.form
              .get('autodeclaracao')
              ?.setValue(obj.autodeclaracao == null || '' ? 'NAO_INFORMADO' : obj.autodeclaracao)
            this.form.get('declaracaoVeracidade')?.setValue(obj.declaracaoVeracidade ? 'true' : 'false')
          } else if (this.tipoConcurso.match('PTPP')) {
            this.form
              .get('autodeclaracao')
              ?.setValue(obj.autodeclaracao == null || '' ? 'NAO_INFORMADO' : obj.autodeclaracao)
            this.form.get('autodeclaraRaca')?.setValue('false')
            this.form.get('pontuacaoDiferenciada')?.setValue('false')
            this.form.get('declaracaoVeracidade')?.setValue('false')
            this.form.get('autodeclaraRaca')?.disable()
            this.form.get('pontuacaoDiferenciada')?.disable()
            this.form.get('declaracaoVeracidade')?.disable()
          } else {
            this.form.get('autodeclaraRaca')?.setValue('false')
            this.form.get('pontuacaoDiferenciada')?.setValue('false')
            this.form.get('autodeclaracao')?.setValue('NAO_INFORMADO')
            this.form.get('declaracaoVeracidade')?.setValue('false')
            this.form.get('autodeclaraRaca')?.disable()
            this.form.get('pontuacaoDiferenciada')?.disable()
            this.form.get('autodeclaracao')?.disable()
            this.form.get('declaracaoVeracidade')?.disable()
          }

          this.form.get('temas')?.setValue(obj.temas)
          this.documentoIdentificacaoFc.value.setNameAndSize(
            obj.documentoIdentificacao,
            obj.documentoIdentificacaoTamanho
          )
          this.documentoIdentificacaoFc.setValue(this.documentoIdentificacaoFc.value)
          this.requerimentoNomeSocialFc.value.setNameAndSize(
            obj.requerimentoNomeSocial,
            obj.requerimentoNomeSocialTamanho
          )
          this.requerimentoNomeSocialFc.setValue(this.requerimentoNomeSocialFc.value)

          this.setupPassaporteRneValidation()
          this.initComplementos(obj.inscricaoComplemento)
          this.initAnexos(
            obj.depositoAnexo,
            obj.reducaoComprovanteEstudante,
            obj.reducaoComprovanteRenda,
            obj.deficienciaLaudoMedico,
            obj.documentoIdentificacao,
            obj.atendimentoEspecialLaudoMedico,
            obj.requerimentoNomeSocial,
            ''
          )

          this.form.disable()
          this.formComplementar.get('inscricao')?.disable()
          this.formComplementar.get('segundaFase')?.disable()
          this.cd.detectChanges()

          // Abrir aba específica se especificada na URL
          if (this.parametroTab === 'complementos') {
            setTimeout(() => {
              const tabIndex = this.getTabIndex('complementos')
              if (tabIndex >= 0 && this.tabGroup) {
                this.tabGroup.selectedIndex = tabIndex
              }
            }, 100)
          }
        })
      }
    })
  }

  private initComplementos(complementos: InscricaoComplemento[]) {
    this.complementos = complementos
    let itensFaltandoInscricaoCount = 0
    let itensFaltandoSegundaFaseCount = 0
    for (let complemento of complementos) {
      if (complemento.etapa === 'SEGUNDA FASE') this.temSegundaFase = true

      let grupoNome = '[' + complemento.etapa + '] ' + complemento.grupo
      if ((complemento.requerido as boolean) && complemento.valor.length == 0) {
        if (complemento.etapa === 'SEGUNDA FASE') itensFaltandoSegundaFaseCount++
        else itensFaltandoInscricaoCount++
      }
      let arr = this.mapGruposComplementos.get(grupoNome)
      arr ? arr.push(complemento) : this.mapGruposComplementos.set(grupoNome, [complemento])
    }
    this.setStatusComplemento(itensFaltandoInscricaoCount, itensFaltandoSegundaFaseCount)
    this.formComplementar.valueChanges.subscribe(() => this.updateComplementoStatus())
  }

  // usado para exibir na tela seguindo a ordem que os elementos foram adicionado no Map
  asIsOrder(a: any, b: any) {
    return 0
  }

  updateComplementoStatus() {
    let itensFaltandoInscricaoCount = 0
    let itensFaltandoSegundaFaseCount = 0
    for (let complemento of this.complementos) {
      if (!(complemento.requerido as boolean)) continue

      let fase = complemento.etapa == 'SEGUNDA FASE' ? 'segundaFase' : 'inscricao'
      let fc = this.formComplementar.get(fase)?.get(complemento.id.toString())
      if (fc == null) continue

      if (complemento.tipoComplemento == 'FILE') {
        const fstatus = fc.value.status
        if (fstatus == ArquivoStatus.ENVIADO || fstatus == ArquivoStatus.SALVO) continue
      } else if (fc.value) continue

      if (complemento.etapa === 'SEGUNDA FASE') itensFaltandoSegundaFaseCount++
      else itensFaltandoInscricaoCount++
    }
    this.setStatusComplemento(itensFaltandoInscricaoCount, itensFaltandoSegundaFaseCount)
  }

  private setStatusComplemento(faltandoPrimeiraFase: number, faltandoSegundaFase: number) {
    {
      let { status, msg } = this.statusComplementoFase(faltandoPrimeiraFase, 'inscrição', this.isPeriodoDeInscricao)
      this.complementosInscricaoStatus = status
      this.complementosInscricaoStatusMsg = msg
    }
    {
      let { status, msg } = this.statusComplementoFase(
        faltandoSegundaFase,
        'segunda fase',
        this.isPeriodoDeInscricao || this.isPeriodoSegundaFase
      )
      this.complementosSegundaFaseStatus = status
      this.complementosSegundaFaseStatusMsg = msg
    }
  }

  private statusComplementoFase(faltando: number, fase: string, isPeriodo: boolean): any {
    let status: 'Completo' | 'Falta' | 'Faltou'
    let msg: string

    if (faltando == 0) {
      status = 'Completo'
      msg = `Todos os itens necessários da ${fase} foram enviados.`
    } else {
      if (isPeriodo) status = 'Falta'
      else status = 'Faltou'

      if (faltando == 1) msg = `${status} o envio/preenchimento de 1 item obrigatório da ${fase}`
      else msg = `${status} o envio/preenchimento de ${faltando} itens obrigatórios da ${fase}`
    }

    return { status, msg }
  }

  private initAnexos(
    deposito: string,
    estudante: string,
    renda: string,
    laudoMedicoPcd: string,
    documentoIdentificacao: string,
    laudoMedicoAtendimentoEspecial: string,
    requerimentoNomeSocial: string,
    documentoJurado: string
  ) {
    let anexos: Anexo[] = []

    if (documentoIdentificacao)
      anexos.push({
        nome: 'Documento de identificação',
        path: `inscricao/${this.id}/documentoidentificacao`,
        fileName: documentoIdentificacao,
      })

    if (requerimentoNomeSocial)
      anexos.push({
        nome: 'Requerimento de uso do nome social',
        path: `inscricao/${this.id}/requerimentonomesocial`,
        fileName: requerimentoNomeSocial,
      })

    if (laudoMedicoPcd)
      anexos.push({
        nome: 'Laudo médico de deficiências',
        path: `inscricao/${this.id}/laudomedicodeficiencia`,
        fileName: laudoMedicoPcd,
      })

    if (laudoMedicoAtendimentoEspecial)
      anexos.push({
        nome: 'Laudo médico de outras necessidades especiais',
        path: `inscricao/${this.id}/laudomedicoatendimentoespecial`,
        fileName: laudoMedicoAtendimentoEspecial,
      })



    if (deposito && this.showDepositoEmAnexos())
      anexos.push({ nome: 'Comprovante de depósito', path: `inscricao/${this.id}/pagamento`, fileName: deposito })

    if (estudante && this.showTabReducao())
      anexos.push({
        nome: 'Comprovante estudante',
        path: `inscricao/${this.id}/comprovanteestudante`,
        fileName: estudante,
      })

    if (renda && this.showTabReducao())
      anexos.push({ nome: 'Comprovante de renda', path: `inscricao/${this.id}/comprovanterenda`, fileName: renda })

    for (const arq of this.complementos) {
      if (arq.tipoComplemento == 'FILE' && arq.valor) {
        let nome = '[' + arq.etapa + '] ' + arq.grupo
        if (arq.explicacao) nome += ': ' + arq.explicacao
        anexos.push({
          nome,
          // path: 'inscricaocomplemento/' + arq.id, // download pelo id do complemento
          path: 'inscricaocomplemento/' + this.idConcurso, // download pelo id do concurso
          obrigatorio: (arq.requerido as boolean) ? 'Obrigatório' : 'Opcional',
          fileName: arq.valor,
          anexoTamanho: Number(arq.anexoTamanho) || 0,
          complementoId: arq.id,
        })
      }
    }

    this.anexos = anexos
  }

  modoEdicao() {
    this.edicaoFicha = !this.edicaoFicha
    if (this.edicaoFicha) {
      this.form.enable()
      this.validaEstrangeiro()
    } else {
      this.form.disable()
      this.formComplementar.disable()
    }
  }

  modoEditarComplementos() {
    this.edicaoComplementos = !this.edicaoComplementos
    if (this.edicaoComplementos) {
      if (this.inscricaoEditavel) this.formComplementar.get('inscricao')?.enable()
      this.formComplementar.get('segundaFase')?.enable()
    } else {
      this.formComplementar.disable()
    }
  }

  applyTouchedToInvalidFields() {
    const arrNameErrors = []
    for (const name in this.form.controls) {
      if (this.form.get(name)?.invalid) {
        this.form.get(name)?.markAsTouched()
        arrNameErrors.push(name?.replace('endereco','')?.replace(/([A-Z])/g,'$1').trim())
      }
    }
    return arrNameErrors
  }

  voltar(): void {
    if (this.concursoId) {
      this.router.navigate(['/concurso', this.concursoId, 'inscricoes'], {
        queryParams: { pagina: this.parametroPagina, tamanho: this.parametroTamanho },
      })
    } else {
      this.router.navigate(['/inscricao'], {
        queryParams: { pagina: this.parametroPagina, tamanho: this.parametroTamanho },
      })
    }
  }

  gravar(): void {
    if (this.edicaoFicha) {
      this.fixCPFValue()
      const arrNameErros = this.applyTouchedToInvalidFields()

      if (!this.form.valid) {
        for(const name in this.form.controls) {
          if (this.form.get(name)?.invalid) {
            this.form.get(name)?.markAsTouched()
          }
        }
        this.unespCoreMessageService.showMessageError(`Erro ao preencher os dados do formulário nos campos: ${arrNameErros.join(', ')}`)
        return
      }
      if (this.id) {
        let formularioTemporario = this.form.getRawValue()
        let { ...formularioFinal } = formularioTemporario
        let formulario = <InscricaoFicha>formularioFinal

        formulario.pago = formulario.pago == 'Confirmado' ? true : false
        formulario.canhoto = formulario.canhoto == 'true' ? true : false
        formulario.declaracaoVeracidade = formulario.declaracaoVeracidade == 'true' ? 'true' : 'false'
        formulario.pontuacaoDiferenciada = formulario.pontuacaoDiferenciada == 'true' ? 'true' : 'false'

        // Preservar campos de arquivo que não estão no form
        formulario.documentoIdentificacao = this.documentoIdentificacaoFc.value.name || this.inscricaoFicha?.documentoIdentificacao || ''
        formulario.documentoIdentificacaoTamanho = this.documentoIdentificacaoFc.value.size || this.inscricaoFicha?.documentoIdentificacaoTamanho || 0
        formulario.deficienciaLaudoMedico = this.laudoMedicoPcdFc.value.name || this.inscricaoFicha?.deficienciaLaudoMedico || ''
        formulario.deficienciaLaudoMedicoTamanho = this.laudoMedicoPcdFc.value.size || this.inscricaoFicha?.deficienciaLaudoMedicoTamanho || 0
        formulario.requerimentoNomeSocial = this.requerimentoNomeSocialFc.value.name || this.inscricaoFicha?.requerimentoNomeSocial || ''
        formulario.requerimentoNomeSocialTamanho = this.requerimentoNomeSocialFc.value.size || this.inscricaoFicha?.requerimentoNomeSocialTamanho || 0
        formulario.atendimentoEspecialLaudoMedico = this.laudoMedicoAtendimentoEspecialFc.value.name || this.inscricaoFicha?.atendimentoEspecialLaudoMedico || ''
        formulario.atendimentoEspecialLaudoMedicoTamanho = this.laudoMedicoAtendimentoEspecialFc.value.size || this.inscricaoFicha?.atendimentoEspecialLaudoMedicoTamanho || 0

        this.inscricaoService.alterar(this.id, formulario).subscribe(inscricaoFicha => {
          if (inscricaoFicha.id) {
            this.unespCoreMessageService.showMessageSuccess(`Ficha #${this.id} atualizada com sucesso`)
          } else {
            this.unespCoreMessageService.showMessageError(
              'Não foi possível salvar, verifique os dados e tente novamente'
            )
          }
        })
      }
    }
  }

  fixCPFValue(): void {
    const cpfControl = this.form.get('cpf')?.value
    if(cpfControl) {
      this.form.get('cpf')?.setValue(this.formatCPF(cpfControl))
    }
  }

  showTabReducao(): boolean {
    return (
      this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS']) && this.inscricaoFicha?.valor != 0
    )
  }

  showTabPagamento(): boolean {
    return (
      this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS']) && this.inscricaoFicha?.valor != 0
    )
  }

  showDepositoEmAnexos(): boolean {
    return (
      this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS', 'DEPARTAMENTO']) &&
      this.inscricaoFicha?.valor != 0
    )
  }

  showTabDevolucao(): boolean {
    return (
      this.unespCoreAuthService.hasPermissionBasedRoles(['FINANCAS']) &&
      (this.inscricaoFicha?.devolucaoSolicitada ?? false) &&
      this.inscricaoFicha?.valor != 0
    )
  }

  showTabSituacao(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA'])
  }

  showModoEdicao(): boolean {
    // M268038-180: Modo edição desativado — candidatos agora editam suas próprias fichas.
    // Manter código para possível reativação futura.
    return false
    // return this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'GERENTE'])
  }

  showTabAnexos(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles([
      'RH',
      'DTA',
      'GERENTE',
      'CCPT',
      'CACP',
      'DEPARTAMENTO',
      'DIRETOR',
    ])
  }

  showTabDocumentos(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'GERENTE'])
  }

  imprimir(): void {
    this.id &&
      this.inscricaoService.gerarFichaImpressao(this.id).subscribe(blob => {
        console.log(blob)
        const blobUrl = window.URL.createObjectURL(blob)
        const iframe = document.createElement('iframe')

        document.body.appendChild(iframe)

        iframe.style.display = 'none'
        iframe.src = blobUrl
        iframe.onload = () => {
          setTimeout(() => {
            iframe.focus()
            iframe.contentWindow?.print()
          }, 1)
        }
      })
  }

  validaCEP(): void {
    const isBrasil = this.form.get('enderecoPais')?.value === 'Brasil'
    this.validacaoService.validaCEP(this.form, isBrasil)
  }

  validaDataNascimento(): void {
    this.validacaoService.validaDataNascimento(this.form)
  }

  validaEstrangeiro(): void {
    this.validacaoService.validaEstrangeiro(this.form)
    this.validaPassaporteRne()
  }
  onRneInput(event: Event): void {
    const input = event.target as HTMLInputElement
    const maskedValue = maskDocumentoMigratorio(input.value)
    this.form.get('rne')?.setValue(maskedValue, { emitEvent: false })
    input.value = maskedValue
  }


  private getTabIndex(tabName: string): number {
    let index = 0

    // Aba "Ficha" sempre existe
    if (tabName === 'ficha') return index
    index++

    // Aba "Complementos"
    if (this.mapGruposComplementos.size > 0 && this.showTabAnexos()) {
      if (tabName === 'complementos') return index
      index++
    }

    // Outras abas...
    if (this.showTabReducao()) {
      if (tabName === 'reducao') return index
      index++
    }

    if (this.showTabPagamento()) {
      if (tabName === 'pagamento') return index
      index++
    }

    if (this.showTabDevolucao()) {
      if (tabName === 'devolucao') return index
      index++
    }

    if (this.showTabSituacao()) {
      if (tabName === 'situacao') return index
      index++
    }

    if (this.documentos.length > 0 && this.showTabDocumentos()) {
      if (tabName === 'documentos') return index
      index++
    }

    if (this.anexos.length > 0 && this.showTabAnexos()) {
      if (tabName === 'anexos') return index
      index++
    }

    if (this.showTabHistorico()) {
      if (tabName === 'historico') return index
      index++
    }


    return -1
  }
  private setupPassaporteRneValidation(): void {
    this.form.get('passaporte')?.valueChanges.subscribe(() => {
      this.validaPassaporteRne()
    })

    this.form.get('rne')?.valueChanges.subscribe(() => {
      this.validaPassaporteRne()
    })

    // Initial validation
    this.validaPassaporteRne()
  }

  private formatCPF(cpf: string): string {
    if (!cpf) return ''
    const cleaned = cpf.replace(/\D/g, '')
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
    return cpf
  }

  private validaPassaporteRne(): void {
    const passaporteControl = this.form.get('passaporte')
    const rneControl = this.form.get('rne')
    const estrangeiroValue = this.form.get('estrangeiro')?.value

    if (estrangeiroValue === 'SIM') {
      const passaporteValue = passaporteControl?.value?.trim()
      const rneValue = rneControl?.value?.trim()

      if (passaporteValue) {
        // If passaporte has value, rne becomes optional
        rneControl?.clearValidators()
      } else if (rneValue) {
        // If rne has value, passaporte becomes optional
        passaporteControl?.clearValidators()
      } else {
        // If both are empty, both are required
        passaporteControl?.setValidators([Validators.required])
        rneControl?.setValidators([Validators.required])
      }

      passaporteControl?.updateValueAndValidity({ emitEvent: false })
      rneControl?.updateValueAndValidity({ emitEvent: false })
    }
  }

  // --- Histórico de alterações ---

  onTabChange(event: any): void {
    if (event.tab?.textLabel === 'Histórico de alterações' && this.historicoLogs.length === 0 && !this.historicoLoading) {
      this.carregarHistorico()
    }
  }

  showTabHistorico(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles([
      'RH', 'DTA', 'GERENTE', 'CCPT', 'CACP', 'DEPARTAMENTO', 'DIRETOR',
    ])
  }

  carregarHistorico(page = 0): void {
    if (!this.id) return
    this.historicoLoading = true
    this.historicoPage = page
    this.inscricaoService.getHistorico(this.id, page, this.historicoPageSize).subscribe({
      next: (res: any) => {
        this.historicoLogs = res.content || []
        this.historicoTotalElements = res.totalElements || 0
        this.historicoLoading = false
      },
      error: () => {
        this.historicoLoading = false
      },
    })
  }

  onHistoricoPaginaAnterior(): void {
    if (this.historicoPage > 0) this.carregarHistorico(this.historicoPage - 1)
  }

  onHistoricoProximaPagina(): void {
    const totalPages = Math.ceil(this.historicoTotalElements / this.historicoPageSize)
    if (this.historicoPage < totalPages - 1) this.carregarHistorico(this.historicoPage + 1)
  }

  traduzirAcao(acao: string): string {
    return this.acaoTraducao[acao] || acao
  }

  isLogUpload(log: any): boolean {
    return log.acao?.includes('INSCRICAO_COMPLEMENTO') || log.acao?.includes('INSCRICAO_ARQUIVO') || log.acao?.includes('INSCRICAO_DOCUMENTO') || log.acao?.includes('RECURSO') || log.acao?.includes('INSCRICAO_UPLOAD')
  }

  extrairSucessoFalha(log: any): { sucesso: boolean; motivo: string } {
    const sucesso = log.nivel === 'Info'
    let motivo = ''
    if (!sucesso && log.detalhesJSON) {
      try {
        const d = JSON.parse(log.detalhesJSON)
        motivo = d.erroMsg || d.descricaoDaAcao || ''
      } catch {
        motivo = ''
      }
    }
    return { sucesso, motivo }
  }

  private estabilidadePrimeiraAbertura = true
  estabilidadeRefPage = 0

  abrirEstabilidade(log: any, page = 0): void {
    if (!this.id) return
    this.estabilidadeLogRef = log
    this.estabilidadeLoading = true
    this.estabilidadePage = page
    this.inscricaoService.getEstabilidade(this.id, log.id, page, 25).subscribe({
      next: (res: any) => {
        this.estabilidadeLogs = res.content || []
        this.estabilidadeTotalElements = res.totalElements || 0
        this.estabilidadeTotalPages = res.totalPages || 0
        this.estabilidadeRefPage = res.refPage || 0
        // Na primeira abertura, ir para a página do log de referência
        if (this.estabilidadePrimeiraAbertura && res.refPage > 0) {
          this.estabilidadePrimeiraAbertura = false
          this.abrirEstabilidade(log, res.refPage)
          return
        }
        this.estabilidadePrimeiraAbertura = false
        setTimeout(() => this.estabilidadeSection?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' }))
        this.estabilidadeLoading = false
      },
      error: () => {
        this.estabilidadeLoading = false
      },
    })
  }

  estabilidadeTotalPages = 0

  get estabilidadePages(): number[] {
    return Array.from({ length: this.estabilidadeTotalPages }, (_, i) => i)
  }

  irParaPaginaEstabilidade(page: number): void {
    this.abrirEstabilidade(this.estabilidadeLogRef, page)
  }

  onEstabilidadePaginaAnterior(): void {
    if (this.estabilidadePage > 0) this.abrirEstabilidade(this.estabilidadeLogRef, this.estabilidadePage - 1)
  }

  onEstabilidadeProximaPagina(): void {
    const totalPages = this.estabilidadeTotalPages
    if (this.estabilidadePage < totalPages - 1) this.abrirEstabilidade(this.estabilidadeLogRef, this.estabilidadePage + 1)
  }

  fecharEstabilidade(): void {
    this.estabilidadeLogRef = null
    this.estabilidadeLogs = []
    this.estabilidadePrimeiraAbertura = true
  }

  isLogDaInscricaoAtual(log: any): boolean {
    return log.registroPai == this.id || (log.registroPai == null && log.registroId == this.id)
  }

  estabilidadeInicio(): Date | null {
    if (!this.estabilidadeLogRef?.dataHora) return null
    return new Date(new Date(this.estabilidadeLogRef.dataHora).getTime() - 30 * 60 * 1000)
  }

  estabilidadeFim(): Date | null {
    if (!this.estabilidadeLogRef?.dataHora) return null
    return new Date(new Date(this.estabilidadeLogRef.dataHora).getTime() + 30 * 60 * 1000)
  }

  copiarEstabilidade(): void {
    const header = 'ID Log\tData e Hora\tID Inscrição\tAção\tSucesso/Falha\tMotivo'
    const rows = this.estabilidadeLogs.map(l => {
      const sf = this.extrairSucessoFalha(l)
      return `${l.id}\t${l.dataHora}\t${l.registroPai || l.registroId || ''}\t${this.traduzirAcao(l.acao)}\t${sf.sucesso ? 'Sucesso' : 'Falha'}\t${sf.motivo}`
    })
    const text = [header, ...rows].join('\n')
    navigator.clipboard.writeText(text)
    this.unespCoreMessageService.showMessageSuccess('Dados copiados para a área de transferência')
  }

  toggleHistoricoInfo(): void {
    this.historicoInfoExpanded = !this.historicoInfoExpanded
  }

}
